import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaPhone,
  FaEnvelope,
  FaUserTie,
  FaIdBadge,
  FaCalendarAlt,
  FaUserGraduate,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaCheck,
  FaTimesCircle,
  FaWhatsapp,
  FaQrcode,
  FaUsers,
  FaEllipsisV,
  FaUserSlash,
  FaUserCheck
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import UserAvatar from '../components/UserAvatar';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faChartBar,
  faUsers as faUsersSolid,
  faListAlt,
  faTicketAlt,
  faUsers,
  faTools,
  faGraduationCap,
  faIdCard,
  faFlag
} from '@fortawesome/free-solid-svg-icons';

function MemberDatabase() {
  // State management
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [dropdownMember, setDropdownMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // Notification system
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    const newNotification = { id, message, type };

    // Add new notification
    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  // Remove notification by id
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  // Open member profile modal
  const openMemberProfile = (member) => {
    setViewingMember(member);
    setIsModalOpen(true);
  };

  // Close member profile modal
  const closeModal = () => {
    setIsModalOpen(false);
    setViewingMember(null);
  };

  // Function to open QR code modal
  const openQRCodeModal = (member, e) => {
    if (e) e.stopPropagation();
    setViewingMember(member);
    setShowQRModal(true);
  };

  // Close modals
  const closeModals = () => {
    setIsModalOpen(false);
    setShowQRModal(false);
    setViewingMember(null);
  };

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    avatar: null
  };

  // Fetch members data
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setIsLoading(true);
        const apiUrl = 'https://dev.sliitleo.org/Backend/member_QRapi.php';
        console.log('Fetching members from:', apiUrl);

        // First try with credentials
        let response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        // If unauthorized, try without credentials
        if (response.status === 401 || response.status === 0) {
          console.log('Trying without credentials...');
          response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
        }

        console.log('Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`HTTP error! Status: ${response.status}: ${errorText}`);
        }

        let responseData;
        try {
          responseData = await response.json();
          // console.log('Raw API response:', responseData);
        } catch (jsonError) {
          console.error('Error parsing JSON:', jsonError);
          throw new Error('Invalid JSON response from server');
        }

        // Check if response is an array or an object with members array
        const membersData = Array.isArray(responseData)
          ? responseData
          : (responseData.members || []);

        // console.log('Processed members data:', membersData);

        if (!membersData.length) {
          console.warn('No members data received or empty array');
          setError('No member data available');
          return;
        }

        // Map the data to our expected format
        const formattedMembers = membersData.map(member => ({
          id: member.SLIITLeoID || member.id || '',
          name: member.FullName || member.name || '',
          SLIITID: member.SLIITID || member.SLIITID || '',
          clubId: member.SLIITLeoID || member.clubId || '',
          faculty: member.Faculty || member.faculty || '',
          year: member.Year || member.year || '',
          semester: member.Semester || member.semester || '',
          status: member.LeoClubStatus || member.status || 'Active',
          myLCI: member.MyLCI || member.myLCI || 0,
          role: member.Role || member.role || 'Club Member',
          registered: member.RegDate || member.registered || '',
          email: member.Email || member.email || '',
          contactNo: member.ContactNo || member.contactNo || member.contact || '',
          dob: member.DOB || member.dob || '',
          address: member.Address || member.address || '',
          postal: member.PostalCode || member.postal || '',
          school: member.School || member.school || '',
          skills: member.Skills || member.skills || '',
          nic: member.NIC || member.nic || ''
        }));

        // console.log('Formatted members:', formattedMembers);
        setMembers(formattedMembers);
        setError(null);
      } catch (err) {
        console.error('Error fetching members:', err);
        setError(`Failed to load members: ${err.message}`);
        showNotification(`Error: ${err.message}`, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, []);

  // Helper to calculate age from DOB string (YYYY-MM-DD or similar)
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';

    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age > 0 ? age + ' years' : 'N/A';
    } catch (e) {
      return 'N/A';
    }
  };

  // Filter members based on search
  const filteredMembers = members.filter(member => {
    // Ensure we have a member object and search term
    if (!member || !search) return true;

    const searchTerm = search.toLowerCase();
    const name = member.name || '';
    const SLIITID = member.SLIITID || '';
    const email = member.email || '';
    const contactNo = member.contactNo || '';

    return (
      name.toString().toLowerCase().includes(searchTerm) ||
      SLIITID.toString().toLowerCase().includes(searchTerm) ||
      email.toString().toLowerCase().includes(searchTerm) ||
      contactNo.toString().includes(search)
    );
  });

  // Pagination
  const pageCount = Math.ceil(filteredMembers.length / rowsPerPage);
  const paginatedMembers = filteredMembers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Toggle member selection
  const toggleMember = (SLIITLeoID) => {
    setSelectedMember(selectedMember === SLIITLeoID ? null : SLIITLeoID);
  };

  // Toggle MYLCI status for a member
  const toggleMyLCI = async (SLIITLeoID, currentStatus) => {
    try {
      const response = await fetch('https://dev.sliitleo.org/Backend/member_QRapi.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'update_myLCI',
          id: SLIITLeoID,
          myLCI: !currentStatus ? 1 : 0,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMembers(prevMembers =>
          prevMembers.map(member =>
            member.SLIITLeoID === SLIITLeoID
              ? { ...member, myLCI: !currentStatus }
              : member
          )
        );
        showNotification(
          `MYLCI status ${!currentStatus ? 'added to' : 'removed from'} member.`,
          'success'
        );
      } else {
        showNotification(
          data.message || 'Failed to update MYLCI status. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error updating MYLCI status:', error);
      showNotification('Failed to update MYLCI status. Please try again.', 'error');
    }
  };

  // Toggle dropdown
  const toggleDropdown = (memberId, e) => {
    if (e) e.stopPropagation();
    setDropdownMember(dropdownMember === memberId ? null : memberId);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex text-white min-h-screen" style={{ background: "var(--bg-base, #070a12)" }}>
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 ml-0 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="panel flex items-center justify-center" style={{ minHeight: 260 }}>
            <span className="spinner lg" role="status" aria-label="Loading members" />
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex text-white min-h-screen" style={{ background: "var(--bg-base, #070a12)" }}>
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 ml-0 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="panel" style={{ padding: 'var(--sp-5)' }}>
            <div className="error-banner">
              <FaTimesCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
              style={{ marginTop: 'var(--sp-4)' }}
            >
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex text-white min-h-screen" style={{ background: "var(--bg-base, #070a12)" }}>
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 ml-0 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="page-head">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 34, height: 34, borderRadius: '999px', background: 'var(--accent)',
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}
              >
                <FaUsers style={{ color: '#fff', fontSize: 14 }} />
              </span>
              Member Database
            </h1>
            <p className="page-desc">Manage and view all members</p>
          </div>
        </div>

        <div className="filter-row" style={{ flexWrap: 'wrap', marginBottom: 'var(--sp-6)' }}>
          <Link to="/members" className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faUsers} /> Members
          </Link>
          <Link to="/events" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faChartPie} /> Events
          </Link>
          <Link to="/scan-qr-code" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faChartBar} /> Scan QR
          </Link>
          <Link to="/attendance" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faListAlt} /> Attendance
          </Link>
        </div>

        {/* Search */}
        <div className="field" style={{ maxWidth: 360 }}>
          <label className="field-label" htmlFor="member-search">Search members</label>
          <div className="relative">
            <FaSearch
              aria-hidden="true"
              style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-3)', fontSize: 13, pointerEvents: 'none',
              }}
            />
            <input
              id="member-search"
              type="text"
              placeholder="Search by name, ID, email, or contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
        </div>

        {/* Members Table */}
        <div className="panel">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Reg No</th>
                  <th scope="col">Email</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Status</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMembers.length > 0 ? (
                  paginatedMembers.map((member) => (
                    <tr
                      key={member.id}
                      onClick={() => toggleMember(member.id)}
                      style={{
                        cursor: 'pointer',
                        background: selectedMember === member.id ? 'var(--surface-2)' : undefined,
                      }}
                    >
                      <td style={{ minWidth: 0 }}>
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            src={member.image}
                            alt={member.name || 'Member'}
                            size={40}
                            ring="var(--surface-1)"
                            fallback="/user.png"
                          />
                          <div className="min-w-0">
                            <div className="truncate" style={{ fontWeight: 600, color: 'var(--text-1)', maxWidth: 200 }} title={member.name}>
                              {member.name}
                            </div>
                            <div className="truncate" style={{ fontSize: 11.5, color: 'var(--text-3)', maxWidth: 200 }} title={member.faculty}>
                              {member.faculty}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="num" style={{ color: 'var(--text-2)' }}>{member.SLIITID || 'N/A'}</td>
                      <td className="truncate" style={{ color: 'var(--text-2)', maxWidth: 220 }} title={member.email || ''}>
                        {member.email || 'N/A'}
                      </td>
                      <td className="num" style={{ color: 'var(--text-2)' }}>{member.contactNo || 'N/A'}</td>
                      <td>
                        <span className={`pill ${member.status === 'Active' ? 'pill-success' : 'pill-danger'}`}>
                          <span className="dot" />
                          {member.status || 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="row-actions">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMemberProfile(member);
                            }}
                            className="icon-btn"
                            aria-label={`View profile for ${member.name || 'member'}`}
                            title="View Profile"
                          >
                            <FaEye style={{ color: 'var(--link)', fontSize: 14 }} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openQRCodeModal(member, e);
                            }}
                            className="icon-btn"
                            aria-label={`Show QR code for ${member.name || 'member'}`}
                            title="QR Code"
                          >
                            <FaQrcode style={{ color: 'var(--accent)', fontSize: 14 }} />
                          </button>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(member.id, e);
                              }}
                              className="icon-btn"
                              aria-label="More options"
                              aria-haspopup="menu"
                              aria-expanded={dropdownMember === member.id}
                              title="More options"
                            >
                              <FaEllipsisV style={{ color: 'var(--text-2)', fontSize: 14 }} />
                            </button>
                            {dropdownMember === member.id && (
                              <div
                                role="menu"
                                className="panel absolute right-0 mt-2 z-10"
                                style={{ width: 200, boxShadow: '0 20px 50px -20px rgba(0,0,0,.7)' }}
                              >
                                <button
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMyLCI(member.id, member.myLCI);
                                    setDropdownMember(null);
                                  }}
                                  className="w-full flex items-center gap-2"
                                  style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12.5 }}
                                >
                                  {member.myLCI ? (
                                    <>
                                      <FaUserSlash style={{ color: 'var(--error)' }} />
                                      Remove from MYLCI
                                    </>
                                  ) : (
                                    <>
                                      <FaUserCheck style={{ color: 'var(--success)' }} />
                                      Add to MYLCI
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty-state">
                        <span className="empty-icon"><FaUsers /></span>
                        <h3>No members found</h3>
                        <p>Try adjusting your search terms.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div
              className="flex items-center justify-between flex-wrap gap-3"
              style={{ padding: 'var(--sp-4) var(--sp-5)', borderTop: '1px solid var(--border)' }}
            >
              <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: 0 }}>
                Showing <span className="num">{(page - 1) * rowsPerPage + 1}</span> to{' '}
                <span className="num">
                  {Math.min(page * rowsPerPage, filteredMembers.length)}
                </span>{' '}
                of <span className="num">{filteredMembers.length}</span> members
              </p>
              <nav className="flex items-center gap-1" aria-label="Pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-ghost btn-sm"
                  aria-label="Previous page"
                >
                  <FaChevronLeft style={{ fontSize: 11 }} />
                </button>
                {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
                  // Show pages around current page
                  let pageNum;
                  if (pageCount <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pageCount - 2) {
                    pageNum = pageCount - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={page === pageNum ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                      aria-current={page === pageNum ? 'page' : undefined}
                      aria-label={`Page ${pageNum}`}
                    >
                      <span className="num">{pageNum}</span>
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                  disabled={page === pageCount}
                  className="btn btn-ghost btn-sm"
                  aria-label="Next page"
                >
                  <FaChevronRight style={{ fontSize: 11 }} />
                </button>
              </nav>
            </div>
          )}
        </div>

        {/* QR Code Modal */}
        {showQRModal && viewingMember && (
          <div className="modal-overlay" onClick={closeModals}>
            <div className="modal-panel panel" onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <h3>Member QR Code</h3>
                <button onClick={closeModals} className="icon-btn" aria-label="Close QR code dialog">
                  <FaTimes />
                </button>
              </div>
              <div
                className="modal-body"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--sp-3)', textAlign: 'center' }}
              >
                <div className="qr-box" style={{ background: '#fff', padding: 16, borderRadius: 'var(--radius-md)', maxWidth: '100%' }}>
                  <QRCodeSVG
                    value={JSON.stringify({
                      id: viewingMember.SLIITLeoID,
                      name: viewingMember.name,
                      SLIITID: viewingMember.SLIITID
                    })}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <h4 style={{ fontSize: 15, fontWeight: 600 }}>{viewingMember.name}</h4>
                <p style={{ color: 'var(--text-2)', margin: 0 }}>{viewingMember.SLIITID}</p>
              </div>
              <div className="modal-foot">
                <button onClick={closeModals} className="btn btn-ghost">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Member Profile Modal */}
        {isModalOpen && viewingMember && (
          <div className="modal-overlay" onClick={closeModals}>
            <div className="modal-panel panel lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <h3>Member Profile</h3>
                <button onClick={closeModals} className="icon-btn" aria-label="Close member profile">
                  <FaTimes />
                </button>
              </div>

              <div className="modal-body">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left Column - Profile */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <UserAvatar
                      src={viewingMember.image}
                      alt={viewingMember.name || 'Member'}
                      size={104}
                      ring="var(--surface-1)"
                      fallback="/user.png"
                    />
                    <h2 style={{ fontSize: 17, fontWeight: 700, textAlign: 'center', marginTop: 'var(--sp-3)' }}>
                      {viewingMember.name}
                    </h2>
                    <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: 0 }}>{viewingMember.SLIITID}</p>

                    {viewingMember.myLCI && (
                      <span className="pill pill-info" style={{ marginTop: 'var(--sp-3)' }}>
                        <span className="dot" />
                        MYLCI Member
                      </span>
                    )}

                    <div className="mt-4 w-full">
                      <button
                        className="btn btn-primary"
                        style={{ width: '100%', justifyContent: 'center' }}
                        onClick={() => openQRCodeModal(viewingMember)}
                      >
                        <FaQrcode /> View QR Code
                      </button>
                    </div>
                  </div>

                  {/* Right Column - Details */}
                  <div className="md:w-2/3">
                    <h4 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 'var(--sp-4)' }}>Personal Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                        <h5 className="eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaIdBadge /> Registration No.
                        </h5>
                        <p style={{ margin: 0 }}>{viewingMember.SLIITID || 'N/A'}</p>
                      </div>

                      <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                        <h5 className="eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaEnvelope /> Email
                        </h5>
                        <p style={{ margin: 0 }}>{viewingMember.email || 'N/A'}</p>
                      </div>

                      <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                        <h5 className="eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaPhone /> Contact No.
                        </h5>
                        <p style={{ margin: 0 }}>{viewingMember.contactNo || 'N/A'}</p>
                      </div>

                      <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                        <h5 className="eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaCalendarAlt /> Date of Birth
                        </h5>
                        <p style={{ margin: 0 }}>
                          {viewingMember.dob ? formatDate(viewingMember.dob) : 'N/A'}
                          {viewingMember.dob && ` (${calculateAge(viewingMember.dob)})`}
                        </p>
                      </div>

                      <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                        <h5 className="eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaUserGraduate /> Faculty
                        </h5>
                        <p style={{ margin: 0 }}>{viewingMember.faculty || 'N/A'}</p>
                      </div>

                      <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                        <h5 className="eyebrow" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaMapMarkerAlt /> Address
                        </h5>
                        <p style={{ margin: 0 }}>{viewingMember.address || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div className="mt-6">
                      <h4 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 'var(--sp-4)' }}>Additional Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                          <h5 className="eyebrow" style={{ marginBottom: 8 }}>Position</h5>
                          <p style={{ margin: 0 }}>{viewingMember.position || 'Member'}</p>
                        </div>

                        <div style={{ background: 'var(--surface-2)', padding: 'var(--sp-4)', borderRadius: 'var(--radius-md)' }}>
                          <h5 className="eyebrow" style={{ marginBottom: 8 }}>Status</h5>
                          <span className={`pill ${viewingMember.status === 'active' ? 'pill-success' : 'pill-danger'}`}>
                            <span className="dot" />
                            {viewingMember.status || 'inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-foot">
                <button onClick={closeModals} className="btn btn-ghost">
                  Close
                </button>
                <button
                  onClick={() => {
                    closeModals();
                    openQRCodeModal(viewingMember);
                  }}
                  className="btn btn-primary"
                >
                  <FaQrcode /> Show QR Code
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2" role="status" aria-live="polite">
        {notifications.map(notification => {
          // Map notification type to the shared banner classes
          const bannerClass = {
            success: 'success-banner',
            error: 'error-banner',
            warning: 'warning-banner',
            info: 'info-banner'
          }[notification.type] || 'info-banner';

          return (
            <div
              key={notification.id}
              className={`${bannerClass} shadow-lg`}
              style={{
                minWidth: 240,
                maxWidth: 320,
                animation: 'slideIn 0.3s ease-out',
                opacity: 1,
                transition: 'opacity 0.3s ease-out',
                transform: 'translateX(0)',
                animationName: 'slideIn',
                animationDuration: '0.3s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards'
              }}
              onAnimationEnd={(e) => {
                if (e.animationName === 'slideOut') {
                  removeNotification(notification.id);
                }
              }}
            >
              {notification.message}
            </div>
          );
        })}
      </div>

      {/* Add global styles */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        .qr-box svg {
          max-width: 100%;
          height: auto;
        }
        @media (max-width: 420px) {
          .qr-box svg {
            width: 160px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default MemberDatabase;
